import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Player } from '../types/club';

interface Message {
  from: 'user' | 'agent';
  text: string;
  timestamp: Date;
  isSearchQuery?: boolean;
}

interface SearchState {
  // Search results
  results: Player[];
  totalResults: number;
  currentPage: number;
  
  // Search context
  messages: Message[];
  filters: Record<string, any>;
  searchQuery: string;
  hasSearched: boolean;
  
  // Actions
  setSearchResults: (results: Player[], total: number, page: number) => void;
  setSearchContext: (messages: Message[], filters: Record<string, any>, query: string) => void;
  setHasSearched: (value: boolean) => void;
  clearSearch: () => void;
  restoreSearch: () => {
    results: Player[];
    totalResults: number;
    currentPage: number;
    messages: Message[];
    filters: Record<string, any>;
    searchQuery: string;
    hasSearched: boolean;
  };
}

const initialState = {
  results: [],
  totalResults: 0,
  currentPage: 1,
  messages: [],
  filters: {},
  searchQuery: '',
  hasSearched: false,
};

export const useSearchStore = create<SearchState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSearchResults: (results, total, page) =>
        set({
          results,
          totalResults: total,
          currentPage: page,
        }),

      setSearchContext: (messages, filters, query) =>
        set({
          messages,
          filters,
          searchQuery: query,
        }),

      setHasSearched: (value) =>
        set({
          hasSearched: value,
        }),

      clearSearch: () =>
        set({
          ...initialState,
        }),

      restoreSearch: () => {
        const state = get();
        return {
          results: state.results,
          totalResults: state.totalResults,
          currentPage: state.currentPage,
          messages: state.messages,
          filters: state.filters,
          searchQuery: state.searchQuery,
          hasSearched: state.hasSearched,
        };
      },
    }),
    {
      name: 'scout-search-storage',
      // Only persist essential data, not the full messages array
      partialize: (state) => ({
        results: state.results,
        totalResults: state.totalResults,
        currentPage: state.currentPage,
        filters: state.filters,
        searchQuery: state.searchQuery,
        hasSearched: state.hasSearched,
        // Store only essential message data to avoid bloat
        messages: state.messages.slice(-5).map(m => ({
          from: m.from,
          text: m.text,
          timestamp: m.timestamp,
          isSearchQuery: m.isSearchQuery,
        })),
      }),
    }
  )
);
